import { useState } from "react";
import { BookOpen, CheckCircle, Share2, Shield, ThumbsDown, ThumbsUp } from "lucide-react";
import { useToast } from "@/components/ui/Toast";
import { useClarifierFeedback } from "@/lib/api";
import { misconceptionPath } from "@/lib/paths";
import type {
  ClarificationResponse as Clarification,
  ClarificationSourceResponse as ClarificationSource,
  MisconceptionDetail,
  MisconceptionSummary,
} from "@/lib/api";

/* ============================================
   Source Card
   ============================================ */

function SourceCard({ src }: { src: ClarificationSource }) {
  const colorMap: Record<string, { bg: string; border: string; label: string }> = {
    quran: { bg: "bg-emerald-50", border: "border-emerald-100", label: "text-emerald-600" },
    hadith_sahih: { bg: "bg-blue-50", border: "border-blue-100", label: "text-blue-600" },
    hadith_hasan: { bg: "bg-blue-50", border: "border-blue-100", label: "text-blue-600" },
    hadith: { bg: "bg-blue-50", border: "border-blue-100", label: "text-blue-600" },
    scholarly: { bg: "bg-amber-50", border: "border-amber-100", label: "text-amber-600" },
    scholarly_consensus: { bg: "bg-amber-50", border: "border-amber-100", label: "text-amber-600" },
    historical: { bg: "bg-orange-50", border: "border-orange-100", label: "text-orange-600" },
  };
  const colors = colorMap[src.sourceType] ?? colorMap.scholarly;

  return (
    <div className={`p-3 rounded-lg border ${colors.bg} ${colors.border}`}>
      <p className="text-xs italic text-[var(--color-ink)] leading-relaxed">"{src.text}"</p>
      {src.textArabic && (
        <p lang="ar" dir="rtl" className="font-arabic text-sm text-[var(--color-gold-dim)]/60 mt-1.5 text-right">{src.textArabic}</p>
      )}
      <p className={`text-[10px] font-semibold mt-1.5 ${colors.label}`}>
        — {src.reference}
        {src.url && (
          <a href={src.url} target="_blank" rel="noreferrer" className="ms-2 font-normal underline opacity-80 hover:opacity-100">
            check the original
          </a>
        )}
      </p>
      {src.gradings.length > 0 && (
        <p className="text-[10px] text-[var(--color-ink-muted)] mt-1">
          {src.gradings
            .map((g) => `${g.grader}${g.byReviewer ? " (reviewing scholar)" : ""}: ${g.grading}`)
            .join(" · ")}
        </p>
      )}
      {src.verifiedAt && src.verifiedBy && (
        <p className="flex items-center gap-1 text-[10px] text-emerald-700 mt-1">
          <CheckCircle className="w-3 h-3" /> Verified by {src.verifiedBy}
        </p>
      )}
    </div>
  );
}

/* ============================================
   Evidence: clarifications and their sources
   ============================================ */

export function MisconceptionEvidence({ detail }: { detail: MisconceptionDetail }) {
  const clarifications: Clarification[] = detail.clarifications ?? [];
  const allSources = clarifications.flatMap((c) => c.sources);

  // Heuristic: separate by heading type
  const historicalContext = clarifications.find(
  (c) => c.title?.toLowerCase().includes("historical") || c.title?.toLowerCase().includes("context"),
  );
  const counterEvidence = clarifications.find(
  (c) => c.title?.toLowerCase().includes("counter") || c.title?.toLowerCase().includes("evidence"),
  );
  const mainClarifications = clarifications.filter(
    (c) => c !== historicalContext && c !== counterEvidence,
  );

  return (
    <div className="pt-5 space-y-4">
      {/* Main Clarification Body */}
      {mainClarifications.length > 0 && (
        <div className="flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-emerald-500 shrink-0 mt-1" />
          <div className="flex-1">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-3">
              Evidence-Based Clarification
            </h4>
            {mainClarifications.map((clar) => (
              <div key={clar.id}>
                {clar.title && !clar.title.toLowerCase().includes("clarification") && (
                  <p className="text-xs font-semibold text-[var(--color-midnight)] mb-2 mt-3">{clar.title}</p>
                )}
                {clar.body.split("\n\n").map((para, i) => (
                  <p key={i} className="text-sm text-[var(--color-ink)] leading-relaxed mb-3">{para}</p>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* If no structured split, show all clarifications */}
      {mainClarifications.length === 0 && clarifications.length > 0 && (
        <div className="flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-emerald-500 shrink-0 mt-1" />
          <div className="flex-1">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-3">
              Evidence-Based Clarification
            </h4>
            {clarifications.map((clar) => (
              <div key={clar.id}>
                {clar.title && <p className="text-xs font-semibold text-[var(--color-midnight)] mb-2 mt-3">{clar.title}</p>}
                {clar.body.split("\n\n").map((para, i) => (
                  <p key={i} className="text-sm text-[var(--color-ink)] leading-relaxed mb-3">{para}</p>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Historical Context */}
      {historicalContext && (
        <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-700 mb-1.5">Historical Context</p>
          <p className="text-sm text-amber-900/80 leading-relaxed">{historicalContext.body}</p>
        </div>
      )}

      {/* Counter-Evidence */}
      {counterEvidence && (
        <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700 mb-3 flex items-center gap-1.5">
            <Shield className="w-3.5 h-3.5" /> KEY COUNTER-EVIDENCE
          </p>
          <ul className="space-y-2">
            {counterEvidence.body.split("\n").filter(Boolean).map((point, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-emerald-800/80">
                <CheckCircle className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                {point.replace(/^[-•]\s*/, "")}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Primary Sources */}
      {allSources.length > 0 && (
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--color-ink-muted)] mb-3 flex items-center gap-1.5">
            <BookOpen className="w-3.5 h-3.5" /> PRIMARY SOURCES ({allSources.length})
          </p>
          <div className="space-y-2.5">
            {allSources.map((src) => (
              <SourceCard key={src.id} src={src} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================================
   Feedback + share
   ============================================ */

export function MisconceptionFeedback({ item }: { item: MisconceptionSummary }) {
  const [helpful, setHelpful] = useState<boolean | null>(null);
  const toast = useToast();
  const feedback = useClarifierFeedback();

  const sendFeedback = (value: boolean) => {
    if (helpful !== null) return;
    setHelpful(value);
    feedback.mutate(
      { id: item.id, helpful: value },
      {
        onSuccess: () => toast.success(value ? "Thank you for your feedback!" : "Thank you — we'll improve this content."),
        onError: () => {
          setHelpful(null);
          toast.error("Couldn't send your feedback. Please try again.");
        },
      },
    );
  };

  const handleShare = async () => {
    const text = `Misconception: "${item.claim}"\n\nFact: ${item.summary}\n\n${window.location.origin}${misconceptionPath(item)}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard for sharing");
    } catch {
      toast.error("Couldn't copy to the clipboard");
    }
  };

  return (
    <div className="flex items-center gap-3 pt-3 mt-4 border-t border-[var(--color-cream-dark)]">
      <span className="text-xs text-[var(--color-ink-muted)] mr-1">Was this helpful?</span>
      <button
        onClick={() => sendFeedback(true)} disabled={helpful !== null} aria-pressed={helpful === true}
        className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full transition-colors ${
          helpful === true ? "bg-emerald-100 text-emerald-700" : "text-[var(--color-ink-muted)] hover:text-emerald-600 hover:bg-emerald-50"
        }`}
      >
        <ThumbsUp className="w-3 h-3" /> Yes
      </button>
      <button
        onClick={() => sendFeedback(false)} disabled={helpful !== null} aria-pressed={helpful === false}
        className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full transition-colors ${
          helpful === false ? "bg-red-100 text-red-600" : "text-[var(--color-ink-muted)] hover:text-red-500 hover:bg-red-50"
        }`}
      >
        <ThumbsDown className="w-3 h-3" /> Needs work
      </button>
      <div className="flex-1" />
      <button
        onClick={() => void handleShare()}
        className="flex items-center gap-1.5 text-xs text-[var(--color-ink-muted)] hover:text-blue-600 transition-colors"
      >
        <Share2 className="w-3 h-3" /> Share
      </button>
    </div>
  );
}
